import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { ChevronLeft, Calendar, Video, Loader2 } from "lucide-react";

export default function ExpertDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [expert, setExpert] = useState(null);
  const [busy, setBusy] = useState(null); // slot id being booked

  useEffect(() => {
    api.get(`/experts/${id}`).then((r) => setExpert(r.data));
  }, [id]);

  const book = async (slot) => {
    setBusy(slot.id);
    try {
      const { data } = await api.post("/bookings", { slot_id: slot.id, session_type: "clinical" });
      toast.success("Booking confirmed");
      nav("/me");
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!expert) return <div className="animate-pulse text-[color:var(--mano-ink-soft)]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="expert-detail">
      <Link to="/experts" className="inline-flex items-center gap-1 uppercase-label" data-testid="expert-back">
        <ChevronLeft size={14} /> All experts
      </Link>
      <div className="rounded-3xl border border-[color:var(--mano-border)] bg-white p-6 lg:p-8 flex gap-6 flex-wrap">
        <img src={expert.avatar} alt={expert.name} className="h-24 w-24 rounded-2xl object-cover" />
        <div className="flex-1 min-w-[240px]">
          <h1 className="font-display text-2xl">{expert.name}</h1>
          <div className="text-[color:var(--mano-ink-soft)] mt-1">{expert.credentials}</div>
          <div className="text-sm text-[color:var(--mano-ink-soft)] mt-1">{expert.affiliation}</div>
          <p className="mt-4 leading-relaxed">{expert.bio}</p>
          <div className="mt-4 text-sm">
            <span className="uppercase-label">Session fee </span>
            <span className="font-semibold ml-2">₹{expert.session_fee_inr}</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[color:var(--mano-border)] bg-white p-6">
        <div className="flex items-center gap-2 uppercase-label"><Calendar size={14} /> Upcoming slots</div>
        {expert.slots?.length ? (
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {expert.slots.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-xl border border-[color:var(--mano-border)] p-4">
                <div>
                  <div className="font-semibold">
                    {new Date(s.start_iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                  </div>
                  <div className="text-sm text-[color:var(--mano-ink-soft)]">
                    {new Date(s.start_iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {" – "}
                    {new Date(s.end_iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <button
                  data-testid={`book-slot-${s.id}`}
                  onClick={() => book(s)}
                  disabled={busy === s.id}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-sm font-semibold disabled:opacity-60"
                >
                  {busy === s.id ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />}
                  Book
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 text-sm text-[color:var(--mano-ink-soft)]">No open slots right now — please check back soon.</div>
        )}
      </div>
    </div>
  );
}
